import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, BookOpen, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConversionCta from "@/components/ConversionCta";
import { usePageMeta } from "@/hooks/usePageMeta";
import { gta6PageBySlug, relatedGta6Pages } from "@/seo/gta6Pages";

export default function Gta6GuidePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = gta6PageBySlug(slug);
  const live = page && page.status === "launch";
  usePageMeta(live ? page : {
    path: `/gta-6/${slug}`,
    title: "Guide not published yet | GamePlatPlay",
    description: "This GTA 6 guide isn't live yet. Browse the GTA 6 hub for guides that are.",
    indexable: false,
  });

  if (!live) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-28 text-center">
        <p className="font-vt323 text-2xl text-gp-gold tracking-widest">404</p>
        <h1 className="font-unbounded font-black tracking-tighter text-4xl mt-3">That GTA 6 guide is not live yet</h1>
        <p className="text-white/60 mt-4">GamePlatPlay is adding GTA 6 pages as official information and player search demand grow.</p>
        <Button onClick={() => navigate("/gta-6")} className="mt-8 rounded-full bg-gp-blue">GTA 6 hub</Button>
      </div>
    );
  }

  const related = relatedGta6Pages(page);

  return (
    <article className="mx-auto max-w-3xl px-5 md:px-8 py-12 md:py-16" data-testid="gta6-guide-page">
      <p className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">GTA 6 · {page.query}</p>
      <h1 className="font-unbounded font-black tracking-tighter text-4xl sm:text-5xl mt-2">{page.h1}</h1>

      <div className="mt-5 flex items-center gap-2 text-xs text-gp-green uppercase tracking-widest">
        <ShieldCheck size={15} /> Confirmed-facts guide
      </div>

      <p className="text-white/75 text-lg mt-5 leading-relaxed">{page.answer}</p>

      {page.bullets?.length > 0 && (
        <ul className="mt-8 space-y-3">
          {page.bullets.map((item) => (
            <li key={item} className="glass rounded-2xl px-5 py-4 text-white/80 leading-relaxed">{item}</li>
          ))}
        </ul>
      )}

      {page.faq?.length > 0 && (
        <section className="mt-10 space-y-4">
          <h2 className="font-unbounded font-bold text-xl">Quick answers</h2>
          {page.faq.map((item) => (
            <div key={item.q} className="rounded-2xl border border-white/10 bg-black/20 px-5 py-4">
              <h3 className="font-semibold">{item.q}</h3>
              <p className="text-sm text-white/65 mt-2 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </section>
      )}

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="font-unbounded font-bold text-xl flex items-center gap-2"><BookOpen size={18} className="text-gp-blue" /> Keep exploring GTA 6</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {related.map((item) => (
              <Link key={item.path} to={item.path} className="glass rounded-2xl px-4 py-3 hover:bg-white/5 transition-colors">
                <span className="text-xs uppercase tracking-widest text-white/40">{item.cluster}</span>
                <p className="font-semibold mt-1">{item.h1}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <Button onClick={() => navigate(`/chat?game=gta6&question=${encodeURIComponent(page.query)}`)} variant="outline" className="rounded-full border-white/15 bg-white/5">
          Ask Yorick about GTA 6 <ArrowRight size={16} className="ml-2" />
        </Button>
        <Link to="/gta-6" className="text-white/60 hover:text-white self-center text-sm">Back to GTA 6 hub</Link>
      </div>

      <ConversionCta source={`gta6-${page.slug}`} />
    </article>
  );
}
